/**
 * The first step of the taste profile onboarding app
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classnames from 'classnames'
import { withRouter } from 'react-router'
import isEqual from 'lodash/isEqual'

import { createStructuredSelector } from 'reselect'
import { selectQuestions, selectBrowser } from '../../selectors'
import { AnimatedFlyingText, LoaderButton } from 'components'
import styles from './start.css'
import headingStyles from 'styles/elements/heading.css'
import bottles from './images/start-bottles.png'
import { clearAnswers, saveGeneratedImageUrls } from './actions'
import { trackTasteProfileEvent } from 'utils/tagManager'
import { preloadImages } from 'utils/preload'

export class Start extends Component {
  static prepareQuestionImagesObject (props) {
    const A_OR_B = ['a', 'b']
    const variant = props.browser.greaterThan.screenSM ? '' : '.360w'
    return props.questions.map((question) => ({
      ...question,
      images: [
        require(`../../images/${question.url}-${A_OR_B[0]}${variant}.jpg`),
        require(`../../images/${question.url}-${A_OR_B[1]}${variant}.jpg`)
      ]
    }))
  }

  static preloadImages (questions) {
    return Promise.all(
      questions.map(({images}) => preloadImages(images))
    )
  }

  constructor (props) {
    super(props)

    const urlParams = window.location.search

    this.state = {
      moveOut: false,
      animationDone: false,
      skipIntro: urlParams.toLowerCase().indexOf('skipintro') !== -1
    }

    if (this.state.skipIntro) {
      window.history.replaceState({}, '', window.location.href.split('?')[0])
    }

    this.processedImages = false
    this.handleClick = this.handleClick.bind(this)
    this.handleAnimationEnd = this.handleAnimationEnd.bind(this)
  }

  async componentDidMount () {
    const { questions } = this.props

    if (!questions || questions.length === 0) {
      return
    }

    this.questions = Start.prepareQuestionImagesObject(this.props)
    this.props.saveGeneratedImageUrls(this.questions)
    this.processedImages = true

    if (this.state.skipIntro) {
      Start.preloadImages(this.questions.slice(0, 1)).then(() => {
        this.navigateToNextQuestion()
        Start.preloadImages(this.questions.slice(1))
      })
    } else {
      Start.preloadImages(this.questions)
    }
  }

  componentWillReceiveProps (nextProps) {
    if (this.processedImages || isEqual(nextProps.questions, this.props.questions)) {
      return
    }
    this.questions = Start.prepareQuestionImagesObject(nextProps)
    this.props.saveGeneratedImageUrls(this.questions)
    Start.preloadImages(this.questions)
  }

  render () {
    const {moveOut, animationDone, skipIntro} = this.state
    const deferAction = !animationDone
    const thereAreQuestions = this.questions && this.questions.length

    if (skipIntro) {
      return null
    }

    return (
      <div className={classnames(styles.wrapper, {[styles.animateOut]: moveOut})}>
        <AnimatedFlyingText
          visible={!moveOut}
          speed={1.1}
          className={headingStyles.h1}
          underlineLines={[1]}
        >
          {[
            'KLAAR OM ACHTER JOUW',
            'FAVORIETE WIJNSMAAK TE KOMEN?'
          ]}
        </AnimatedFlyingText>

        <p className={classnames(styles.intro, {[styles.flyOut]: moveOut})}>
          Heb je hulp nodig bij het kiezen van de juiste fles wijn voor jou?
          Beantwoord in deze test 13 vragen en kom er zo achter welke wijnsmaak nu echt bij je past.
          Sla je smaakprofiel op en ontvang tijdelijk <mark className={styles.highlight}>25% korting</mark> op twee flessen naar keuze!*
        </p>

        <div className={classnames(styles.buttonWrapper, {[styles.flyOut]: moveOut})}>
          <LoaderButton
            disabled={!thereAreQuestions}
            clickHandler={this.handleClick}
            deferAction={deferAction}
            {...trackTasteProfileEvent('Start de test', 'Start de test')}
          >
            Start de test
          </LoaderButton>
        </div>
        <img
          src={bottles}
          alt="Verschillende soorten wijnflessen"
          className={classnames(styles.image, {[styles.flyOut]: moveOut})}
        />
        <p
          className={classnames(styles.asterisk, {[styles.flyOut]: moveOut})}
          onAnimationEnd={moveOut ? this.handleAnimationEnd : () => {}}
        >
          *25% korting geldt alleen op wijn en geldt niet i.c.m. andere aanbiedingen.
          De korting is éénmalig te gebruiken en niet inwisselbaar voor contanten.
          Bij (een vermoeden van) fraude of misbruik heeft Gall & Gall het recht de coupon te
          weigeren.
        </p>
      </div>
    )
  }

  handleAnimationEnd () {
    this.setState({animationDone: true})
    this.navigateToNextQuestion()
  }

  handleClick () {
    this.setState({moveOut: true})
    this.props.clearAnswers()
  }

  navigateToNextQuestion () {
    const {nextUrl} = this.props
    const url = `${process.env.WEBSHOP_SMAAKPROFIELEN_PATH}/doe-de-test/${nextUrl}`
    this.props.router.push(url)
  }
}

Start.propTypes = {
  // Because of cloneElement we can't use `isRequired`
  nextUrl: PropTypes.string,
  clearAnswers: PropTypes.func.isRequired,
  questions: PropTypes.array.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  saveGeneratedImageUrls: PropTypes.func.isRequired
}

Start.defaultProps = {
  nextUrl: ''
}

const mapDispatchToProps = (dispatch) => ({
  clearAnswers: () => dispatch(clearAnswers()),
  saveGeneratedImageUrls: (images) => dispatch(saveGeneratedImageUrls(images))
})

const mapStateToProps = createStructuredSelector({
  questions: selectQuestions,
  browser: selectBrowser
})

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Start))
